import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createChannelApi,
  updateChannelApi,
  deleteChannelApi,
  updateMessageApi,
  deleteMessageApi,
  postMessageApi,
  markChannelAsReadApi,
  updateChannelInteractedApi,
  addMemberToChannelApi,
  leaveChannelApi,
} from '../api/chatApi'
import { type Channel, type Message } from '../utils/types'
import LogTool from '../../../logger/logTools'
import { chatChannelKeys, chatMessageKeys } from '../utils/queryKeys'

const log = new LogTool({ context: 'ChatMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new channel
 */
export function useCreateChannel(): UseMutationResult<Channel, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createChannelApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Channel created successfully')
    },
    onError: (error) => {
      log.error('Error creating channel:', error)
    },
  })
}

/**
 * Hook to update a channel
 */
export function useUpdateChannel(): UseMutationResult<
  Channel,
  Error,
  { channelId: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ channelId, data }) => updateChannelApi(channelId, data),
    onSuccess: async (updatedChannel) => {
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      await queryClient.invalidateQueries({ 
        queryKey: chatChannelKeys.detail(Number(updatedChannel.key))
      })
      log.info('Channel updated successfully')
    },
    onError: (error) => {
      log.error('Error updating channel:', error)
    },
  })
}

/**
 * Hook to delete a channel
 */
export function useDeleteChannel(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteChannelApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Channel deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting channel:', error)
    },
  })
}

/**
 * Hook to post a message to a channel
 */
export function usePostMessage(): UseMutationResult<
  Message,
  Error,
  { channelId: string | number; messageData: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ channelId, messageData }) => postMessageApi(channelId, messageData),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ 
        queryKey: chatMessageKeys.lists()
      })
      await queryClient.invalidateQueries({
        queryKey: chatChannelKeys.detail(typeof variables.channelId === 'number' ? variables.channelId : Number(variables.channelId))
      })
      log.info('Message posted successfully')
    },
    onError: (error) => {
      log.error('Error posting message:', error)
    },
  })
}

/**
 * Hook to mark a channel as read
 */
export function useMarkChannelAsRead(): UseMutationResult<
  void,
  Error,
  string | number
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markChannelAsReadApi,
    onSuccess: async (_, channelId) => {
      await queryClient.invalidateQueries({
        queryKey: chatChannelKeys.detail(typeof channelId === 'number' ? channelId : Number(channelId))
      })
      log.info('Channel marked as read')
    },
    onError: (error) => {
      log.error('Error marking channel as read:', error)
    },
  })
}

/**
 * Hook to update channel interacted timestamp
 */
export function useUpdateChannelInteracted(): UseMutationResult<
  void,
  Error,
  string | number
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateChannelInteractedApi,
    onSuccess: async (_, channelId) => {
      await queryClient.invalidateQueries({
        queryKey: chatChannelKeys.detail(typeof channelId === 'number' ? channelId : Number(channelId))
      })
      log.info('Channel interacted timestamp updated')
    },
    onError: (error) => {
      log.error('Error updating channel interacted timestamp:', error)
    },
  })
}

/**
 * Hook to add a member to a channel
 */
export function useAddMemberToChannel(): UseMutationResult<
  void,
  Error,
  { membersUrl: string; userSelf: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ membersUrl, userSelf }) => addMemberToChannelApi(membersUrl, userSelf),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Member added to channel successfully')
    },
    onError: (error) => {
      log.error('Error adding member to channel:', error)
    },
  })
}

/**
 * Hook to leave a channel
 */
export function useLeaveChannel(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: leaveChannelApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Left channel successfully')
    },
    onError: (error) => {
      log.error('Error leaving channel:', error)
    },
  })
}

/**
 * Hook to update/edit a message
 */
export function useUpdateMessage(): UseMutationResult<
  Message,
  Error,
  { messageSelf: string; messageData: { body: string } }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ messageSelf, messageData }) => updateMessageApi(messageSelf, messageData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatMessageKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Message updated successfully')
    },
    onError: (error) => {
      log.error('Error updating message:', error)
    },
  })
}

/**
 * Hook to delete a message (soft delete)
 */
export function useDeleteMessage(): UseMutationResult<
  Message,
  Error,
  { messageSelf: string; deletedText: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ messageSelf, deletedText }) => deleteMessageApi(messageSelf, deletedText),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatMessageKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: chatChannelKeys.lists() })
      log.info('Message deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting message:', error)
    },
  })
}

